import React, { useEffect } from 'react';
import { useCurrentEditor, useEditorState } from '@tiptap/react';
import { findParentNode } from '@tiptap/core';
import { IconButton, Stack, SvgIcon, Tooltip, Typography } from '@mui/material';
import { RiMergeCellsHorizontal } from 'react-icons/ri';
import { RiSplitCellsHorizontal } from 'react-icons/ri';
import { RiLayoutRowFill } from 'react-icons/ri';
import BorderAllIcon from '@mui/icons-material/BorderAll';
import BorderBottomIcon from '@mui/icons-material/BorderBottom';
import BorderClearIcon from '@mui/icons-material/BorderClear';
import BorderInnerIcon from '@mui/icons-material/BorderInner';
import BorderLeftIcon from '@mui/icons-material/BorderLeft';
import BorderOuterIcon from '@mui/icons-material/BorderOuter';
import BorderRightIcon from '@mui/icons-material/BorderRight';
import BorderStyleIcon from '@mui/icons-material/BorderStyle';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import BorderTopIcon from '@mui/icons-material/BorderTop';
import AddColumnIcon from '@/components/icons/AddColumnIcon';
import RemoveColumnIcon from '@/components/icons/RemoveColumnIcon';
import AddRowIcon from '@/components/icons/AddRowIcon';
import RemoveRowIcon from '@/components/icons/RemoveRowIcon';
import RemoveTableIcon from '@/components/icons/RemoveTableIcon';
import TableColourPicker from './TableColourPicker';
import { TEMPLATE_VALUE } from '../Extensions/TemplateValue';
import BorderSelect from './BorderSelect';
import BorderWidthSelect from './BorderWidthSelect';
import { BORDER_COLOUR } from '../Extensions/data';

const WrappedIcon = ({ Icon, ...props }) => {
	return (
		<SvgIcon {...props}>
			<Icon />
		</SvgIcon>
	);
};

const TooltipWrapper = ({ title, children }) => {
	return (
		<Tooltip title={title} disableInteractive>
			<span>{children}</span>
		</Tooltip>
	);
};

const TooltipIcon = ({ title, onClick, children, disabled, ...props }) => {
	return (
		<TooltipWrapper title={title}>
			<IconButton onClick={onClick} size='small' disabled={disabled} {...props}>
				{children}
			</IconButton>
		</TooltipWrapper>
	);
};

export const isMenuVisible = ({ editor, state, view }) => {
	if (!['table', 'th', 'tr', 'td'].some((v) => editor?.isActive(v))) return false;
	const { from } = state.selection;

	const resolved = view.domAtPos(from);
	const domNode = resolved?.node;
	if (!domNode) return false;

	let el = domNode.nodeType === 1 ? domNode : domNode.parentElement;

	const editorConfig = editor?.storage?.config?.editor ?? {};

	while (el) {
		let editable = true;
		if (typeof editorConfig.editable === 'function') {
			editable = editorConfig.editable(el?.attributes);
		} else if (editorConfig.editable === false) {
			editable = false;
		}

		if (!editable) {
			return false;
		}

		if (el.getAttribute?.('data-readonly') === 'true') {
			return false;
		}

		el = el.parentElement;
	}

	return true;
};

const TableMenu = () => {
	const { editor } = useCurrentEditor();

	const { isActiveHeader, currentCell, menuVisible, mergeable } = useEditorState({
		editor,
		selector: (ctx) => {
			const selection = ctx.editor?.state?.selection;

			const menuVisible = isMenuVisible({
				editor: ctx.editor,
				state: ctx.editor?.state,
				view: ctx.editor?.view,
			});

			return {
				isActiveHeader:
					ctx.editor &&
					(ctx.editor?.isActive('th') ||
						!!findParentNode((node) => node.type.name === 'tableHeader')(selection)),
				isActive: ['table', 'th', 'tr', 'td'].some((v) => ctx.editor?.isActive(v)),
				currentSelection: selection,
				currentCell: findParentNode((node) =>
					['tableHeader', 'tableCell'].includes(node.type.name)
				)(selection),
				menuVisible,
				mergeable: selection.ranges.length > 1,
			};
		},
	});

	const currentCellSplittable = (() => {
		const colspan = currentCell?.node?.attrs?.colspan ?? 1;
		const rowspan = currentCell?.node?.attrs?.rowspan ?? 1;
		return colspan > 1 || rowspan > 1;
	})();

	if (!menuVisible) return <></>;

	return (
		<Stack spacing={1}>
			<Typography sx={{ fontSize: '10px', fontWeight: 'bold' }}>Table</Typography>
			<Stack
				direction='row'
				rowGap={1}
				columnGap={1}
				useFlexGap
				alignItems={'center'}
				sx={(theme) => ({
					flexWrap: 'wrap',
					width: '100%',

					'& .MuiIconButton-root': {
						padding: '0px',
						transition: 'color 0.15s ease',

						'&:hover': {
							backgroundColor: 'transparent !important',
							color: theme.palette.primary.main,
						},

						'&.active, &.is-active': {
							color: theme.palette.primary.main,
						},

						'& .MuiTouchRipple-root': {
							display: 'none',
						},
					},
					'& .MuiSvgIcon-root': {
						fontSize: '22px',
					},
				})}
			>
				<TooltipIcon
					title='Add column before'
					onClick={() => editor.chain().focus().addColumnBefore().run()}
				>
					<AddColumnIcon direction='left' />
				</TooltipIcon>
				<TooltipIcon
					title='Add column after'
					onClick={() => editor.chain().focus().addColumnAfter().run()}
				>
					<AddColumnIcon direction='right' />
				</TooltipIcon>
				<TooltipIcon
					title='Delete column'
					onClick={() => editor.chain().focus().deleteColumn().run()}
				>
					<RemoveColumnIcon />
				</TooltipIcon>

				<TooltipIcon
					title='Add row before'
					disabled={isActiveHeader}
					onClick={() => editor.chain().focus().addRowBefore().run()}
				>
					<AddRowIcon direction='top' />
				</TooltipIcon>

				<TooltipIcon
					title='Add row after'
					onClick={() => editor.chain().focus().addRowAfter().run()}
				>
					<AddRowIcon direction='bottom' />
				</TooltipIcon>

				<TooltipIcon
					title='Delete row'
					onClick={() => editor.chain().focus().deleteRow().run()}
				>
					<RemoveRowIcon />
				</TooltipIcon>
				{/* Merge cell should only happen if more than one cell is selected */}
				<TooltipIcon
					title='Merge cells'
					disabled={!mergeable}
					onClick={() => editor.chain().focus().mergeCells().run()}
				>
					<WrappedIcon Icon={RiMergeCellsHorizontal} size='small' />
				</TooltipIcon>
				{/* Split cell should only happen if cell has colspan > 1 */}
				<TooltipIcon
					title='Split cells'
					disabled={!currentCellSplittable}
					onClick={() => editor.chain().focus().splitCell().run()}
				>
					<WrappedIcon Icon={RiSplitCellsHorizontal} size='small' />
				</TooltipIcon>
				<TooltipIcon
					title='Toggle header row'
					onClick={() => {
						editor
							.chain()
							.focus()
							.toggleHeaderRow()
							.toggleHeaderRowBold()
							.applyHeaderRowStyles()
							.run();
					}}
				>
					<WrappedIcon Icon={RiLayoutRowFill} size='small' />
				</TooltipIcon>
				<TooltipWrapper title='Change cell colour'>
					<TableColourPicker />
				</TooltipWrapper>
				{/* <Button
                            onClick={() => editor.chain().focus().setCellAttribute('colspan', 2).run()}
                        >
                            Set cell attribute
                        </Button> */}
				<TooltipIcon
					title='Border all'
					onClick={() =>
						editor
							.chain()
							.focus()
							.setCellBorders({ top: true, bottom: true, left: true, right: true })
							.run()
					}
				>
					<BorderAllIcon />
				</TooltipIcon>
				<TooltipIcon
					title='Border clear'
					onClick={() =>
						editor
							.chain()
							.focus()
							.setCellAttribute('borderColor', BORDER_COLOUR)
							.setCellAttribute('borderStyle', 'solid')
							.setCellAttribute('borderWidth', '1px')
							.setCellBorders({
								top: false,
								bottom: false,
								left: false,
								right: false,
							})
							.run()
					}
				>
					<BorderClearIcon />
				</TooltipIcon>
				<TooltipIcon
					title='Border outer'
					onClick={() => editor.chain().focus().setOuterBorders().run()}
				>
					<BorderOuterIcon />
				</TooltipIcon>
				<TooltipIcon
					title='Border inner'
					onClick={() => editor.chain().focus().setInnerBorders().run()}
				>
					<BorderInnerIcon />
				</TooltipIcon>
				<TooltipIcon
					title='Border bottom'
					onClick={() => editor.chain().focus().setCellBorders({ bottom: true }).run()}
				>
					<BorderBottomIcon />
				</TooltipIcon>
				<TooltipIcon
					title='Border top'
					onClick={() => editor.chain().focus().setCellBorders({ top: true }).run()}
				>
					<BorderTopIcon />
				</TooltipIcon>
				<TooltipIcon
					title='Border left'
					onClick={() => editor.chain().focus().setCellBorders({ left: true }).run()}
				>
					<BorderLeftIcon />
				</TooltipIcon>
				<TooltipIcon
					title='Border right'
					onClick={() => editor.chain().focus().setCellBorders({ right: true }).run()}
				>
					<BorderRightIcon />
				</TooltipIcon>
				<TooltipWrapper title='Border colour'>
					<TableColourPicker Icon={BorderColorIcon} attribute='borderColor' />
				</TooltipWrapper>
				<Tooltip title='Border style'>
					<BorderSelect />
				</Tooltip>
				<Tooltip title='Border width'>
					<BorderWidthSelect />
				</Tooltip>
				<TooltipIcon
					title='Delete table'
					onClick={() => editor.chain().focus().deleteTable().run()}
				>
					<RemoveTableIcon />
				</TooltipIcon>
			</Stack>
		</Stack>
	);
};

export default TableMenu;
